import React from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// Button — variants: primary | ghost | soft | danger | small | link
// Usage:
//   <Button variant="primary" icon={Plus}>Neue Frage</Button>
//   <Button variant="ghost">Abbrechen</Button>
//   <Button variant="danger" icon={Trash2}>Löschen</Button>
//   <Button variant="link" icon={ChevronLeft}>Zurück</Button>
// ─────────────────────────────────────────────────────────────────────────────

const STYLES = {
  primary:
    'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[9px] text-[13px] font-semibold ' +
    'text-white bg-[#e85d1f] hover:bg-[#d04f15] disabled:opacity-50 disabled:cursor-not-allowed ' +
    'shadow-[0_1px_0_rgba(20,20,16,.04),0_6px_14px_-8px_rgba(232,93,31,.55)] ' +
    'transition-colors active:scale-[.98]',
  ghost:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] text-[13px] font-semibold ' +
    'text-[#3a3733] bg-white border border-[#e7e5df] hover:bg-[#f5f4f0] hover:border-[#d6d3cb] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  // Tinted secondary: reads as the row's main action without competing with `primary`.
  soft:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] text-[13px] font-semibold ' +
    'text-[#b3542f] bg-[#fdeee6] border border-[#f5c7b0] hover:bg-[#fbe0d1] hover:border-[#eeab8a] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  danger:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] text-[13px] font-semibold ' +
    'text-[#b3321c] bg-white border border-[#e7e5df] hover:bg-[#fbeae6] hover:border-[#f0c9c0] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  small:
    'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] font-semibold ' +
    'text-[#3a3733] bg-white border border-[#e7e5df] hover:bg-[#f5f4f0] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  link:
    'inline-flex items-center gap-1 text-[12.5px] font-medium text-[#e85d1f] ' +
    'hover:text-[#7a2b08] hover:bg-[#fff1e8] rounded-md px-2 py-0.5 transition-colors',
}

const ICON_SIZE = {
  primary: 14,
  ghost:   13,
  soft:    13,
  danger:  13,
  small:   12,
  link:    12,
}

export const Button = React.forwardRef(function Button(
  { variant = 'primary', icon: Icon, iconRight: IconRight, className = '', children, ...rest },
  ref,
) {
  const base = STYLES[variant] || STYLES.primary
  const size = ICON_SIZE[variant] || 13
  return (
    <button
      ref={ref}
      type="button"
      className={base + (className ? ' ' + className : '')}
      {...rest}
    >
      {Icon && <Icon size={size}/>}
      {children}
      {IconRight && <IconRight size={size}/>}
    </button>
  )
})
